import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, CheckCircle, Sparkles, X, Plus, Trash2, Mail, Phone, QrCode } from 'lucide-react';
import { toast } from 'sonner';
import { QRCodeSVG } from 'qrcode.react';

import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../../components/ui/select';
import { Checkbox } from '../../../components/ui/checkbox';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../../components/ui/dialog';

import { surveysAPI } from '../api';
import OrgLocationPicker from './OrgLocationPicker';
import EnabledLanguagesPicker from '../../../components/shared/EnabledLanguagesPicker';
import { useAuth } from '../../../lib/auth';
import { stringifyDetail } from '../../../lib/utils';

const STEPS = ['template', 'name', 'questions', 'alerts', 'review'];

/**
 * Guided Setup Wizard for Survey creation.
 * 5 steps: Template → Name → Questions → Alerts → Review.
 * Pre-selects "restroom" template per Step 11 / default-template requirement.
 */
const SurveyWizard = ({ open, onOpenChange, organizationId, onCreated }) => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const { user } = useAuth();
    // v3.5.10 — Always pass the caller's organization_id when available so
    // the backend never has to guess. Super admin without an org_id stays
    // null (backend handles the fallback).
    const effectiveOrgId = organizationId || user?.organization_id || null;
    const [stepIdx, setStepIdx] = useState(0);
    const [templates, setTemplates] = useState([]);
    const [picked, setPicked] = useState('restroom');
    const [nameEn, setNameEn] = useState('');
    const [nameFr, setNameFr] = useState('');
    const [nameEs, setNameEs] = useState('');
    const [wizardOrgId, setWizardOrgId] = useState('');
    const [selectedLocation, setSelectedLocation] = useState('');
    const [pickerMeta, setPickerMeta] = useState({ loaded: false, orgCount: 0, locationCount: 0 });
    const [questions, setQuestions] = useState([]);
    const [emails, setEmails] = useState('');
    const [phones, setPhones] = useState('');
    const [threshold, setThreshold] = useState(3);
    const [enabledLanguages, setEnabledLanguages] = useState(null); // null = inherit from org
    const [createdSurvey, setCreatedSurvey] = useState(null);
    const [busy, setBusy] = useState(false);

    const lang = i18n.language || 'en';

    useEffect(() => {
        if (!open) return;
        surveysAPI.listTemplates().then(d => setTemplates(d.templates || [])).catch(() => {});
        // reset state on each open (org/location handled by OrgLocationPicker)
        setStepIdx(0);
        setPicked('restroom');
        setNameEn(''); setNameFr(''); setNameEs('');
        setWizardOrgId(effectiveOrgId || '');
        setSelectedLocation('');
        setEmails(''); setPhones(''); setThreshold(3);
        setEnabledLanguages(null);
        setCreatedSurvey(null);
        setQuestions([]);
    }, [open]);

    // When user advances out of step 1 (template), seed default questions
    useEffect(() => {
        if (stepIdx < 2) return;
        if (questions.length > 0) return;
        // Pull default questions from chosen template
        const tpl = templates.find(t2 => t2.template_id === picked);
        if (!tpl) return;
        // We don't have the questions in the metadata; fetch via creating survey draft is wasteful.
        // Simpler: keep questions empty here, the user can confirm in step 3.
        // We'll fetch the full template if needed — safe via the same listTemplates result if we extend it later.
    }, [stepIdx, picked, templates, questions.length]);

    // (Slug is auto-generated server-side — no client-side sync needed.)

    const next = () => setStepIdx(i => Math.min(i + 1, STEPS.length - 1));
    const back = () => setStepIdx(i => Math.max(i - 1, 0));

    const needsLocationPrompt = pickerMeta.locationCount > 1 && !selectedLocation;

    const canAdvance = () => {
        if (stepIdx === 0) return !!picked;
        const primaryName = lang === 'fr' ? nameFr : lang === 'es' ? nameEs : nameEn;
        if (stepIdx === 1) return !!primaryName.trim() && !!wizardOrgId && !needsLocationPrompt;
        return true;
    };

    const handlePublish = async () => {
        if (!wizardOrgId) {
            toast.error(t('surveys.selectOrganizationFirst', 'Please select an organization first.'));
            return;
        }
        if (needsLocationPrompt) {
            toast.error(t('surveys.selectLocationFirst', 'Please select a location before publishing.'));
            return;
        }
        const primaryName = lang === 'fr' ? nameFr : lang === 'es' ? nameEs : nameEn;
        if (!primaryName.trim()) {
            toast.error(t('surveys.requiredFields'));
            return;
        }
        setBusy(true);
        let created = null;
        try {
            // Admin typed in their UI language only — other locales populated by background translation.
            const nameObj = { en: '', fr: '', es: '' };
            nameObj[lang] = primaryName;
            const payload = {
                template: picked,
                organization_id: wizardOrgId || effectiveOrgId || undefined,
                name: nameObj,
                enabled_languages: enabledLanguages,
                source_language: lang,
            };
            if (selectedLocation) payload.location_id = selectedLocation;
            created = await surveysAPI.create(payload);
        } catch (err) {
            // CREATE failed — nothing was persisted. Show the error and let user retry.
            // eslint-disable-next-line no-console
            console.error('SurveyWizard CREATE failed', err, err?.response);
            const msg = stringifyDetail(err?.response?.data?.detail)
                || err?.message
                || t('surveys.createFailed');
            toast.error(msg);
            setBusy(false);
            return;
        }

        // Survey IS created at this point. Apply alerts in a follow-up update.
        // v3.5.13 — If the follow-up PUT fails (timeout, backend transient issue),
        // we still show success because the survey exists and is already discoverable
        // in the list. Admin can retry the alerts save from the editor without losing
        // anything. This stops "Publish froze" perception when step-2 is slow.
        try {
            const updates = {};
            const emailList = emails.split(',').map(s => s.trim()).filter(Boolean);
            const phoneList = phones.split(',').map(s => s.trim()).filter(Boolean).map(p => ({ phone_number: p }));
            if (emailList.length) updates.notification_emails = emailList;
            if (phoneList.length) updates.notification_phones = phoneList;
            updates.alert_rules = {
                trigger_on_low_rating: true,
                low_rating_threshold: parseInt(threshold, 10) || 3,
                trigger_on_negative_choice: false,
            };
            const updated = await surveysAPI.update(created.id, updates);
            setCreatedSurvey(updated);
            toast.success(t('surveys.created'));
        } catch (err) {
            // eslint-disable-next-line no-console
            console.error('SurveyWizard alerts update failed (survey already created)', err, err?.response);
            setCreatedSurvey(created);
            toast.success(t('surveys.createdAlertsLater', 'Survey created. You can set alerts from the editor.'));
        } finally {
            onCreated?.();
            setBusy(false);
        }
    };


    const publicUrl = createdSurvey ? `${window.location.origin}/s/${createdSurvey.slug}` : '';

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-2xl">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        <Sparkles className="w-5 h-5 text-fuchsia-600 dark:text-fuchsia-400" />
                        {t('surveys.wizardTitle')}
                    </DialogTitle>
                    <ProgressBar idx={stepIdx} total={STEPS.length} />
                </DialogHeader>

                {!createdSurvey ? (
                    <>
                        {stepIdx === 0 && (
                            <Step title={t('surveys.wStep1Title')} description={t('surveys.wStep1Desc')}>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    {templates.map(tpl => (
                                        <button
                                            key={tpl.template_id}
                                            type="button"
                                            onClick={() => setPicked(tpl.template_id)}
                                            data-testid={`wizard-template-${tpl.template_id}`}
                                            className={`text-left p-4 rounded-lg border-2 transition-all ${
                                                picked === tpl.template_id
                                                    ? 'border-fuchsia-500 bg-fuchsia-50 dark:bg-fuchsia-950/40'
                                                    : 'border-slate-200 dark:border-slate-700 hover:border-fuchsia-300 dark:hover:border-fuchsia-700'
                                            }`}
                                        >
                                            <div className="font-semibold mb-1">{tpl.display_name?.[lang] || tpl.display_name?.en || tpl.template_id}</div>
                                            <div className="text-xs text-muted-foreground">{tpl.description?.[lang] || tpl.description?.en}</div>
                                            <div className="text-[10px] mt-2 text-fuchsia-600 dark:text-fuchsia-400">
                                                {tpl.default_questions_count || 0} {t('surveys.wDefaultQuestions')}
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            </Step>
                        )}

                        {stepIdx === 1 && (
                            <Step title={t('surveys.wStep2Title')} description={t('surveys.wStep2Desc')}>
                                <div className="space-y-3">
                                    <Field label={t('surveys.nameLabel', 'Survey name') + ' *'}>
                                        <Input
                                            value={lang === 'fr' ? nameFr : lang === 'es' ? nameEs : nameEn}
                                            onChange={(e) => {
                                                const v = e.target.value;
                                                if (lang === 'fr') setNameFr(v);
                                                else if (lang === 'es') setNameEs(v);
                                                else setNameEn(v);
                                            }}
                                            data-testid="wizard-name"
                                            placeholder={t('surveys.namePlaceholder', 'e.g. Bathroom Feedback')}
                                        />
                                        <p className="text-xs text-muted-foreground mt-1">
                                            {t('surveys.autoTranslateHint', 'Other languages will be translated automatically after save.')}
                                        </p>
                                    </Field>
                                    <OrgLocationPicker
                                        organizationId={wizardOrgId}
                                        locationId={selectedLocation}
                                        onChange={({ organizationId: o, locationId: l }) => {
                                            setWizardOrgId(o);
                                            setSelectedLocation(l || '');
                                        }}
                                        onMeta={setPickerMeta}
                                        testIdPrefix="wizard"
                                    />
                                    <div className="pt-2 border-t border-slate-100 dark:border-slate-700">
                                        <EnabledLanguagesPicker
                                            value={enabledLanguages}
                                            onChange={setEnabledLanguages}
                                            allowInherit
                                            inheritedFrom={['en','fr','es']}
                                            testIdPrefix="wizard-languages"
                                        />
                                    </div>
                                    <p className="text-xs text-muted-foreground" data-testid="wizard-url-managed-note">
                                        {t('surveys.urlManagedHelp', 'Your public survey link is generated and managed automatically from your organization, location and survey name — you never have to create or edit a URL.')}
                                    </p>
                                </div>
                            </Step>
                        )}

                        {stepIdx === 2 && (
                            <Step title={t('surveys.wStep3Title')} description={t('surveys.wStep3Desc')}>
                                <div className="rounded-md border border-emerald-200 dark:border-emerald-900/50 bg-emerald-50 dark:bg-emerald-950/30 p-3 text-sm text-emerald-900 dark:text-emerald-200">
                                    <CheckCircle className="w-4 h-4 inline mr-1.5" />
                                    {t('surveys.wStep3Note')}
                                </div>
                            </Step>
                        )}

                        {stepIdx === 3 && (
                            <Step title={t('surveys.wStep4Title')} description={t('surveys.wStep4Desc')}>
                                <div className="space-y-3">
                                    <Field label={t('surveys.threshold')}>
                                        <Input type="number" min={1} max={5} value={threshold} onChange={(e) => setThreshold(e.target.value)} className="w-24" data-testid="wizard-threshold" />
                                    </Field>
                                    <Field label={<><Mail className="w-4 h-4 inline mr-1" />{t('surveys.alertEmails')}</>}>
                                        <Input placeholder="manager@business.com" value={emails} onChange={(e) => setEmails(e.target.value)} data-testid="wizard-emails" />
                                    </Field>
                                    <Field label={<><Phone className="w-4 h-4 inline mr-1" />{t('surveys.alertPhones')}</>}>
                                        <Input placeholder="+15145551234" value={phones} onChange={(e) => setPhones(e.target.value)} data-testid="wizard-phones" />
                                    </Field>
                                </div>
                            </Step>
                        )}

                        {stepIdx === 4 && (
                            <Step title={t('surveys.wStep5Title')} description={t('surveys.wStep5Desc')}>
                                <ReviewBlock label={t('surveys.template')} value={templates.find(tt => tt.template_id === picked)?.display_name?.[lang] || picked} />
                                <ReviewBlock label={t('surveys.nameEn')} value={nameEn} />
                                <ReviewBlock label={t('surveys.threshold')} value={threshold} />
                                {emails && <ReviewBlock label={t('surveys.alertEmails')} value={emails} />}
                                {phones && <ReviewBlock label={t('surveys.alertPhones')} value={phones} />}
                            </Step>
                        )}
                    </>
                ) : (
                    <Step title={t('surveys.wDoneTitle')} description={t('surveys.wDoneDesc')}>
                        <div className="flex flex-col items-center gap-4 py-2">
                            <div className="bg-white dark:bg-slate-100 p-3 rounded-md border" data-testid="wizard-qr">
                                <QRCodeSVG value={publicUrl} size={180} />
                            </div>
                            <p className="text-xs text-muted-foreground break-all text-center font-mono">{publicUrl}</p>
                        </div>
                    </Step>
                )}

                <DialogFooter className="flex flex-row justify-between sm:justify-between gap-2 mt-4">
                    {!createdSurvey ? (
                        <>
                            <Button variant="outline" onClick={stepIdx === 0 ? () => onOpenChange(false) : back} data-testid="wizard-back-btn">
                                {stepIdx === 0 ? t('common.cancel') : <><ChevronLeft className="w-4 h-4 mr-1.5" />{t('common.back')}</>}
                            </Button>
                            {stepIdx < STEPS.length - 1 ? (
                                <Button onClick={next} disabled={!canAdvance()} data-testid="wizard-next-btn">
                                    {t('common.next')}
                                    <ChevronRight className="w-4 h-4 ml-1.5" />
                                </Button>
                            ) : (
                                <Button onClick={handlePublish} disabled={busy || !canAdvance()} data-testid="wizard-publish-btn">
                                    <CheckCircle className="w-4 h-4 mr-1.5" />
                                    {busy ? t('common.saving') : t('surveys.publish')}
                                </Button>
                            )}
                        </>
                    ) : (
                        <>
                            <Button variant="outline" onClick={() => { onOpenChange(false); navigate(`/surveys/${createdSurvey.id}`); }} data-testid="wizard-edit-btn">
                                {t('surveys.editSettings')}
                            </Button>
                            <Button onClick={() => onOpenChange(false)} data-testid="wizard-close-btn">
                                {t('common.done')}
                            </Button>
                        </>
                    )}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

const ProgressBar = ({ idx, total }) => (
    <div className="flex gap-1 mt-2">
        {Array.from({ length: total }).map((_, i) => (
            <div
                key={i}
                className={`h-1 flex-1 rounded-full transition-colors ${i <= idx ? 'bg-fuchsia-500 dark:bg-fuchsia-400' : 'bg-slate-200 dark:bg-slate-700'}`}
            />
        ))}
    </div>
);

const Step = ({ title, description, children }) => (
    <div className="space-y-4 py-2">
        <div>
            <h3 className="text-base font-semibold">{title}</h3>
            <p className="text-sm text-muted-foreground mt-1">{description}</p>
        </div>
        {children}
    </div>
);

const Field = ({ label, children, hint }) => (
    <div className="space-y-1">
        <Label className="text-sm">{label}</Label>
        {children}
        {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
    </div>
);

const ReviewBlock = ({ label, value }) => (
    <div className="flex items-center justify-between py-2 border-b border-slate-100 dark:border-slate-700 last:border-0">
        <span className="text-sm text-muted-foreground">{label}</span>
        <span className="text-sm font-medium text-right break-all max-w-[60%]">{value}</span>
    </div>
);

export default SurveyWizard;
